import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, CheckCircle, ThumbsUp, ThumbsDown, Pencil, Trash2, Download, Package, Search } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { priorityAccent } from '@/components/ui/mobile-card';
import {
  workOrdersApi, equipmentApi, usersApi, tasksApi, partsApi,
  type WorkOrder, type WorkOrderPart,
} from '@/lib/api';
import { uploadStagedAttachments } from '@/lib/attachments';
import { exportToExcel, exportToCsv } from '@/lib/excel';
import { fmtDateTime } from '@/lib/date';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input, Select, Textarea } from '@/components/ui/input';
import { Badge, statusColor, priorityColor } from '@/components/ui/badge';
import { Table, Thead, Tbody, Th, Td, Tr } from '@/components/ui/table';
import { Dialog } from '@/components/ui/dialog';
import { StagedAttachments } from '@/components/StagedAttachments';
import { PageHeader } from '@/components/ui/page-header';


const BLANK: Partial<WorkOrder> = {
  title: '', description: '', priority: 'medium', status: 'open',
  equipment_id: '', custom_equipment_name: '', assigned_technician_id: '',
  actual_start: '', actual_end: '', downtime_minutes: undefined,
  is_planned: true,
};

export function WorkOrdersPage() {
  const { isAdmin, isSupervisor, timezone, user } = useAuth();
  const isTechOnly = !isAdmin && !isSupervisor;
  // Tech can take action on a WO if it's assigned to them. Supervisors/admins
  // can act on any WO.
  const canActOn = (wo: WorkOrder) =>
    isSupervisor || isAdmin || (isTechOnly && wo.assigned_technician_id === user?.id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<WorkOrder | null>(null);
  const [form, setForm] = useState<Partial<WorkOrder>>(BLANK);
  const [signOffId, setSignOffId] = useState<string | null>(null);
  const [signOffNote, setSignOffNote] = useState('');
  const [signOffLabor, setSignOffLabor] = useState<string>('');
  const [signOffDowntime, setSignOffDowntime] = useState<string>('');
  // Tech walk-up combined form: extra state for sign-off fields and parts
  // captured at create time so the tech does everything in one save.
  const [walkupLabor, setWalkupLabor] = useState<string>('');
  const [walkupDowntime, setWalkupDowntime] = useState<string>('');
  const [walkupNotes, setWalkupNotes] = useState<string>('');
  const [walkupParts, setWalkupParts] = useState<Array<{ part_id: string; quantity: string }>>([]);
  // Staged files for the create form — used by both the standard and walk-up
  // create flows; uploaded to the new WO after it's created.
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  // Parts panel
  const [partsWoId, setPartsWoId] = useState<string | null>(null);
  const [partsWoTitle, setPartsWoTitle] = useState('');
  const [addPartId, setAddPartId] = useState('');
  const [addPartQty, setAddPartQty] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading } = useQuery({
    queryKey: ['work-orders', page, filterStatus, search],
    queryFn: () => workOrdersApi.list(page, 50, filterStatus || undefined, undefined, search || undefined),
  });

  const { data: equipment } = useQuery({
    queryKey: ['equipment', 'all'],
    queryFn: () => equipmentApi.list(1, 200),
  });

  const { data: users } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.list(1),
    enabled: isAdmin,
  });

  const { data: tasks } = useQuery({
    queryKey: ['tasks', 'all'],
    queryFn: () => tasksApi.list(1, 200),
  });

  const { data: allParts } = useQuery({
    queryKey: ['parts', 'all'],
    queryFn: () => partsApi.list(1, 200),
    // Need the parts list for the WO parts dialog AND the "Parts used" section
    // on the create form (walk-up and standard alike).
    enabled: !!partsWoId || (open && !editing),
  });

  // Detail of the selected WO (to get up-to-date parts list)
  const { data: partsWo } = useQuery({
    queryKey: ['work-orders', 'detail', partsWoId],
    queryFn: () => workOrdersApi.get(partsWoId!),
    enabled: !!partsWoId,
    refetchInterval: 5_000,
  });

  const save = useMutation({
    mutationFn: async (): Promise<{ failed: string[] }> => {
      const payload = {
        ...form,
        assigned_technician_id: form.assigned_technician_id || null,
        maintenance_task_id: form.maintenance_task_id || null,
      };
      if (editing) {
        await workOrdersApi.update(editing.id, payload);
        return { failed: [] };
      }

      // Create. For tech walk-up flow, also add parts and sign-off in
      // one go so the tech doesn't have a 3-step click sequence. We pass
      // suppress_walkup_notification so the backend skips the "WO created"
      // notification — the sign-off notification that fires a moment later
      // covers supervisor visibility.
      const createPayload = isTechOnly
        ? ({ ...payload, suppress_walkup_notification: true } as Partial<WorkOrder>)
        : payload;
      const newWo = await workOrdersApi.create(createPayload);

      // Upload any staged files (both the standard and walk-up create flows).
      const { failed } = await uploadStagedAttachments('work_order', newWo.id, stagedFiles);

      // Add any parts listed on the create form (both standard and walk-up),
      // best-effort and sequential.
      for (const wp of walkupParts) {
        if (!wp.part_id || !wp.quantity) continue;
        const qty = parseInt(wp.quantity, 10);
        if (!qty || qty <= 0) continue;
        await workOrdersApi.addPart(newWo.id, wp.part_id, qty);
      }

      if (isTechOnly) {
        // Walk-up: sign off so the WO lands in Awaiting Approval immediately.
        const labor    = walkupLabor    ? parseFloat(walkupLabor)        : undefined;
        const downtime = walkupDowntime ? parseInt(walkupDowntime, 10)  : undefined;
        await workOrdersApi.signOff(newWo.id, walkupNotes || '', undefined, labor, downtime);
      }
      return { failed };
    },
    onSuccess: ({ failed }) => {
      qc.invalidateQueries({ queryKey: ['work-orders'] });
      qc.invalidateQueries({ queryKey: ['parts'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      // Reset walk-up + staged-file state
      setWalkupLabor(''); setWalkupDowntime(''); setWalkupNotes(''); setWalkupParts([]); setStagedFiles([]);
      closeDialog();
      if (failed.length) {
        alert(`${failed.length} file${failed.length > 1 ? 's' : ''} couldn't be uploaded: ${failed.join(', ')}. You can add ${failed.length > 1 ? 'them' : 'it'} from the work order.`);
      }
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => workOrdersApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['work-orders'] }),
  });

  const signOff = useMutation({
    mutationFn: () => workOrdersApi.signOff(
      signOffId!,
      signOffNote,
      undefined,
      signOffLabor    ? parseFloat(signOffLabor)    : undefined,
      signOffDowntime ? parseInt(signOffDowntime, 10) : undefined,
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['work-orders'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      setSignOffId(null); setSignOffNote(''); setSignOffLabor(''); setSignOffDowntime('');
    },
  });

  const approve = useMutation({
    mutationFn: (id: string) => workOrdersApi.approve(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['work-orders'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const reject = useMutation({
    mutationFn: () => workOrdersApi.reject(rejectId!, rejectReason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['work-orders'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      setRejectId(null); setRejectReason('');
    },
  });

  const addPart = useMutation({
    mutationFn: () => workOrdersApi.addPart(partsWoId!, addPartId, addPartQty),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['work-orders', 'detail', partsWoId] });
      setAddPartId(''); setAddPartQty(1);
    },
  });

  const removePart = useMutation({
    mutationFn: ({ woId, partId }: { woId: string; partId: string }) =>
      workOrdersApi.removePart(woId, partId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['work-orders', 'detail', partsWoId] }),
  });

  const openCreate = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const openEdit = (wo: WorkOrder) => { setEditing(wo); setForm(wo); setOpen(true); };
  const closeDialog = () => {
    setOpen(false); setEditing(null); setForm(BLANK); setStagedFiles([]);
    setWalkupLabor(''); setWalkupDowntime(''); setWalkupNotes(''); setWalkupParts([]);
  };
  const set = (k: keyof WorkOrder, v: unknown) => setForm(f => ({ ...f, [k]: v }));

  const canSignOff = (wo: WorkOrder) =>
    (wo.status === 'open' || wo.status === 'in_progress');
  const canApproveReject = (wo: WorkOrder) =>
    isSupervisor && wo.status === 'awaiting_approval';

  const woParts: WorkOrderPart[] = partsWo?.parts ?? [];

  const totalCount = data?.pagination.total ?? 0;
  const openCount = data?.data.filter(w => w.status === 'open' || w.status === 'in_progress').length ?? 0;
  const tagText = totalCount === 0
    ? undefined
    : openCount > 0
      ? `${totalCount} total · ${openCount} open`
      : `${totalCount} total`;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Work Orders"
        tag={tagText}
        actions={
          <Button size="sm" onClick={openCreate}>
            <Plus size={14} /> New Work Order
          </Button>
        }
      />

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-sm border border-canvas-line bg-white rounded-lg w-full sm:w-56 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <Select
          value={filterStatus}
          onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
          className="py-1 text-xs"
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="awaiting_approval">Awaiting Approval</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="closed">Closed</option>
        </Select>
        {(['Excel', 'CSV'] as const).map(fmt => (
                <Button
                  key={fmt}
                  variant="outline" size="sm"
                  onClick={async () => {
                    const all = await workOrdersApi.list(1, 5000, filterStatus || undefined);
                    const rows = all.data.map(wo => ({
                      wo_number:        wo.wo_number,
                      title:            wo.title,
                      status:           wo.status,
                      priority:         wo.priority,
                      equipment:        wo.equipment?.name ?? wo.custom_equipment_name ?? '',
                      technician:       wo.assigned_technician?.full_name ?? '',
                      scheduled_start:  wo.scheduled_start ? new Date(wo.scheduled_start).toLocaleDateString() : '',
                      actual_start:     wo.actual_start    ? new Date(wo.actual_start).toLocaleDateString()    : '',
                      actual_end:       wo.actual_end      ? new Date(wo.actual_end).toLocaleDateString()      : '',
                      labor_hours:      wo.labor_hours     ?? '',
                      downtime_min:     wo.downtime_minutes ?? '',
                      cost:             wo.cost            ?? '',
                      completion_notes: wo.completion_notes ?? '',
                    }));
                    fmt === 'Excel' ? exportToExcel(rows, 'work_orders_export') : exportToCsv(rows, 'work_orders_export');
                  }}
                >
                  <Download size={14} /> {fmt}
                </Button>
              ))}
      </div>

      {isLoading ? (
        <p className="py-8 text-center text-slate-400">Loading…</p>
      ) : (
        <>
              {/* Mobile + tablet: card grid */}
              <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-2">
                {data?.data.length === 0 && (
                  <p className="text-center text-slate-400 text-sm py-6 col-span-full">No work orders found.</p>
                )}
                {data?.data.map(wo => (
                  <div
                    key={wo.id}
                    onClick={() => navigate(`/work-orders/${wo.id}`)}
                    className="bg-white rounded-lg border border-slate-200 p-3 cursor-pointer transition-colors active:bg-slate-50"
                    style={{ borderLeftWidth: '3px', borderLeftColor: priorityAccent(wo.priority) }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <span className="font-mono text-[10px] text-slate-400">{wo.wo_number}</span>
                        <p className="font-semibold text-slate-900 text-sm leading-snug truncate">{wo.title}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0 flex-wrap justify-end">
                        <Badge color={statusColor(wo.status)}>{wo.status.replace(/_/g, ' ')}</Badge>
                        <Badge color={priorityColor(wo.priority)}>{wo.priority}</Badge>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 truncate">
                      {wo.equipment?.name ?? wo.custom_equipment_name ?? 'No equipment'}
                      {wo.assigned_technician?.full_name && <> · {wo.assigned_technician.full_name}</>}
                    </p>
                    {wo.scheduled_start && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        Scheduled: {fmtDateTime(wo.scheduled_start, timezone)}
                      </p>
                    )}
                    {(isSupervisor || isAdmin || canSignOff(wo)) && (
                      <div
                        className="flex items-center gap-1 flex-wrap mt-3 pt-3 border-t border-slate-100"
                        onClick={e => e.stopPropagation()}
                      >
                        {isSupervisor && (
                          <button onClick={() => openEdit(wo)} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-slate-100 text-slate-600">
                            <Pencil size={12} /> Edit
                          </button>
                        )}
                        {canActOn(wo) && (
                          <button
                            onClick={() => { setPartsWoId(wo.id); setPartsWoTitle(wo.wo_number); }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-brand-50 text-brand-600"
                          >
                            <Package size={12} /> Parts
                          </button>
                        )}
                        {canSignOff(wo) && (
                          <button
                            onClick={() => { setSignOffId(wo.id); setSignOffNote(''); }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-green-50 text-green-600"
                          >
                            <CheckCircle size={12} /> Sign off
                          </button>
                        )}
                        {canApproveReject(wo) && (
                          <>
                            <button
                              onClick={() => approve.mutate(wo.id)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-green-50 text-green-600"
                            >
                              <ThumbsUp size={12} /> Approve
                            </button>
                            <button
                              onClick={() => { setRejectId(wo.id); setRejectReason(''); }}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-red-50 text-red-600"
                            >
                              <ThumbsDown size={12} /> Reject
                            </button>
                          </>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => { if (confirm(`Delete WO ${wo.wo_number}?`)) del.mutate(wo.id); }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop: table */}
              <div className="hidden lg:block">
            <Table variant="cards">
              <Thead>
                <tr>
                  <Th>WO #</Th><Th>Title</Th><Th>Equipment</Th>
                  <Th>Priority</Th><Th>Status</Th><Th>Technician</Th>
                  <Th>Scheduled</Th><Th>Created</Th><Th className="w-32">Actions</Th>
                </tr>
              </Thead>
              <Tbody>
                {data?.data.map(wo => (
                  <Tr
                    key={wo.id}
                    onClick={e => {
                      if ((e.target as HTMLElement).closest('button, a')) return;
                      navigate(`/work-orders/${wo.id}`);
                    }}
                  >
                    <Td className="font-mono text-xs">
                      <Link to={`/work-orders/${wo.id}`} className="text-brand-600 hover:underline">
                        {wo.wo_number}
                      </Link>
                    </Td>
                    <Td className="font-medium max-w-[12rem] truncate">
                      <Link to={`/work-orders/${wo.id}`} className="hover:text-brand-600">
                        {wo.title}
                      </Link>
                    </Td>
                    <Td className="text-xs">{wo.equipment?.name ?? wo.custom_equipment_name ?? '—'}</Td>
                    <Td><Badge color={priorityColor(wo.priority)}>{wo.priority}</Badge></Td>
                    <Td><Badge color={statusColor(wo.status)}>{wo.status.replace(/_/g, ' ')}</Badge></Td>
                    <Td className="text-xs">{wo.assigned_technician?.full_name ?? '—'}</Td>
                    <Td className="text-xs">{fmtDateTime(wo.scheduled_start, timezone)}</Td>
                    <Td className="text-xs">{fmtDateTime(wo.created_at, timezone)}</Td>
                    <Td>
                      <div className="flex gap-1 flex-wrap">
                        {isSupervisor && (
                          <button onClick={() => openEdit(wo)} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Edit"><Pencil size={13} /></button>
                        )}
                        {canActOn(wo) && (
                          <button
                            onClick={() => { setPartsWoId(wo.id); setPartsWoTitle(wo.wo_number); }}
                            className="p-1 rounded hover:bg-brand-50 text-brand-500"
                            title="Manage parts"
                          ><Package size={13} /></button>
                        )}
                        {canSignOff(wo) && (
                          <button
                            onClick={() => { setSignOffId(wo.id); setSignOffNote(''); }}
                            className="p-1 rounded hover:bg-green-50 text-green-600"
                            title="Sign off"
                          ><CheckCircle size={13} /></button>
                        )}
                        {canApproveReject(wo) && (
                          <>
                            <button
                              onClick={() => approve.mutate(wo.id)}
                              className="p-1 rounded hover:bg-green-50 text-green-600"
                              title="Approve"
                            ><ThumbsUp size={13} /></button>
                            <button
                              onClick={() => { setRejectId(wo.id); setRejectReason(''); }}
                              className="p-1 rounded hover:bg-red-50 text-red-600"
                              title="Reject"
                            ><ThumbsDown size={13} /></button>
                          </>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => { if (confirm(`Delete WO ${wo.wo_number}?`)) del.mutate(wo.id); }}
                            className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                            title="Delete"
                          ><Trash2 size={13} /></button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
                {data?.data.length === 0 && (
                  <Tr><Td className="py-8 text-center text-slate-400" colSpan={8}>No work orders found.</Td></Tr>
                )}
              </Tbody>
            </Table>
              </div>
            </>
          )}

      {(data?.pagination.total ?? 0) > 50 && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>Prev</Button>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page * 50 >= (data?.pagination.total ?? 0)}>Next</Button>
        </div>
      )}

      {/* Create/Edit WO */}
      <Dialog open={open} onClose={closeDialog} title={editing ? `Edit ${editing.wo_number}` : (isTechOnly ? 'Log Completed Work' : 'New Work Order')} className="max-w-2xl">
        <form onSubmit={e => { e.preventDefault(); save.mutate(); }} className="flex flex-col gap-3">
          {/* Role/mode banner: techs log completed work; admins/supervisors pick planned vs unplanned. */}
          {!editing && isTechOnly && (
            <p className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              You're logging work you've completed — it'll go to your supervisor to approve.
            </p>
          )}
          {!editing && !isTechOnly && (
            <div>
              <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm">
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, is_planned: true, status: 'open', actual_start: '' }))}
                  className={`px-4 py-1.5 ${form.is_planned ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                >
                  Planned
                </button>
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, is_planned: false, status: 'in_progress', actual_start: f.actual_start || new Date().toISOString().slice(0, 16) }))}
                  className={`px-4 py-1.5 border-l border-slate-300 ${!form.is_planned ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                >
                  Unplanned
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {form.is_planned
                  ? 'Scheduling upcoming work (PM or planned repair).'
                  : 'Logging reactive work — a breakdown or walk-up that already happened.'}
              </p>
            </div>
          )}
          <Input label="Title *" value={form.title ?? ''} onChange={e => set('title', e.target.value)} required />
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <Select
                label="Equipment *"
                value={form.equipment_id === null || form.equipment_id === undefined ? (form.custom_equipment_name ? '__custom__' : '') : form.equipment_id}
                onChange={e => {
                  if (e.target.value === '__custom__') {
                    set('equipment_id', null);
                  } else {
                    set('equipment_id', e.target.value);
                    set('custom_equipment_name', null);
                  }
                }}
                required={!form.custom_equipment_name}
              >
                <option value="">Select equipment…</option>
                {equipment?.data.map(eq => (
                  <option key={eq.id} value={eq.id}>{eq.asset_tag} — {eq.name}</option>
                ))}
                <option disabled>──────────────</option>
                <option value="__custom__">✏ Not in system — enter manually</option>
              </Select>
              {(form.equipment_id === null || form.equipment_id === undefined) && (
                <Input
                  label="Equipment Name *"
                  value={form.custom_equipment_name ?? ''}
                  onChange={e => set('custom_equipment_name', e.target.value)}
                  placeholder="e.g. Roof exhaust fan unit 3"
                  required
                />
              )}
            </div>
            <Select label="Priority *" value={form.priority ?? 'medium'} onChange={e => set('priority', e.target.value)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </Select>
            {isAdmin && users && (
              <Select label="Assigned Technician" value={form.assigned_technician_id ?? ''} onChange={e => set('assigned_technician_id', e.target.value)}>
                <option value="">Unassigned</option>
                {users.data.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </Select>
            )}
            {!isTechOnly && (
              <Select label="Linked Task" value={form.maintenance_task_id ?? ''} onChange={e => set('maintenance_task_id', e.target.value)}>
                <option value="">None</option>
                {tasks?.data.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            )}
            {!isTechOnly && (
              <Select label="Status" value={form.status ?? 'open'} onChange={e => set('status', e.target.value)}>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="closed">Closed</option>
              </Select>
            )}
            {/* Planned work: scheduled dates. Shown for planned WOs and whenever editing. */}
            {!isTechOnly && (editing || form.is_planned) && (
              <>
                <Input label="Scheduled Start" type="datetime-local" value={form.scheduled_start?.slice(0, 16) ?? ''} onChange={e => set('scheduled_start', e.target.value || undefined)} />
                <Input label="Scheduled End" type="datetime-local" value={form.scheduled_end?.slice(0, 16) ?? ''} onChange={e => set('scheduled_end', e.target.value || undefined)} />
              </>
            )}
            {/* Already-done fields: for unplanned logging, and whenever editing. */}
            {!isTechOnly && (editing || !form.is_planned) && (
              <>
                <Input label="Actual Start" type="datetime-local" value={form.actual_start?.slice(0, 16) ?? ''} onChange={e => set('actual_start', e.target.value || undefined)} />
                <Input label="Actual End" type="datetime-local" value={form.actual_end?.slice(0, 16) ?? ''} onChange={e => set('actual_end', e.target.value || undefined)} />
                <Input label="Labor Hours" type="number" step="0.5" min={0} value={form.labor_hours ?? ''} onChange={e => set('labor_hours', e.target.value ? parseFloat(e.target.value) : undefined)} />
                <Input label="Downtime (minutes)" type="number" min={0} value={form.downtime_minutes ?? ''} onChange={e => set('downtime_minutes', e.target.value ? parseInt(e.target.value) : undefined)} />
              </>
            )}
          </div>
          {isTechOnly && (
            <>
              <p className="text-xs text-slate-500 -mt-2">
                This walk-up WO will be assigned to you. Fill in actuals below — on save it&apos;s submitted for supervisor approval.
              </p>

              <div className="grid grid-cols-2 gap-3 border-t pt-3">
                <Input label="Labor Hours" type="number" step="0.5" min={0} value={walkupLabor}    onChange={e => setWalkupLabor(e.target.value)}    placeholder="e.g. 1.5" />
                <Input label="Downtime (minutes)" type="number" min={0} value={walkupDowntime} onChange={e => setWalkupDowntime(e.target.value)} placeholder="e.g. 30" />
              </div>
              <Textarea label="Completion Notes" value={walkupNotes} onChange={e => setWalkupNotes(e.target.value)} placeholder="What was done, root cause, observations…" />
            </>
          )}
          <Textarea label="Description" value={form.description ?? ''} onChange={e => set('description', e.target.value)} />
          {!editing && (
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-700">Parts used (optional)</label>
                <button
                  type="button"
                  onClick={() => setWalkupParts(prev => [...prev, { part_id: '', quantity: '1' }])}
                  className="text-xs text-brand-600 hover:underline"
                >+ Add part</button>
              </div>
              {walkupParts.length === 0 && (
                <p className="text-xs text-slate-400 italic">No parts added — click &quot;Add part&quot; to log parts used on this WO.</p>
              )}
              {walkupParts.map((wp, idx) => (
                <div key={idx} className="flex gap-2 items-end mb-2">
                  <div className="flex-1">
                    <Select
                      value={wp.part_id}
                      onChange={e => setWalkupParts(prev => prev.map((x, i) => i === idx ? { ...x, part_id: e.target.value } : x))}
                    >
                      <option value="">Select part…</option>
                      {allParts?.data.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.part_number} — {p.name} (stock: {p.stock_quantity})
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="w-24">
                    <Input type="number" min={1} value={wp.quantity} onChange={e => setWalkupParts(prev => prev.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))} />
                  </div>
                  <button
                    type="button"
                    onClick={() => setWalkupParts(prev => prev.filter((_, i) => i !== idx))}
                    className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                  >Remove</button>
                </div>
              ))}
            </div>
          )}
          {!editing && (
            <div className="border-t pt-3">
              <StagedAttachments files={stagedFiles} onChange={setStagedFiles} label="Attachments (optional)" />
            </div>
          )}
          {/* Completion Notes only on the admin/supervisor form — techs use
              the walk-up version above. */}
          {!isTechOnly && (
            <Textarea label="Completion Notes" value={form.completion_notes ?? ''} onChange={e => set('completion_notes', e.target.value)} placeholder="What was done, root cause, observations…" />
          )}
          {save.error && <p className="text-sm text-red-600">{(save.error as Error).message}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={closeDialog}>Cancel</Button>
            <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </Dialog>

      {/* Sign-off dialog */}
      <Dialog open={!!signOffId} onClose={() => setSignOffId(null)} title="Sign Off Work Order">
        <div className="flex flex-col gap-3">
          <p className="text-sm text-slate-600">
            Complete this work order. It will move to "Awaiting Approval" until a supervisor approves it.
          </p>
          <Textarea label="Completion Note" value={signOffNote} onChange={e => setSignOffNote(e.target.value)} placeholder="What was done, root cause, observations…" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Labor Hours" type="number" step="0.5" min={0} value={signOffLabor}    onChange={e => setSignOffLabor(e.target.value)}    placeholder="e.g. 1.5" />
            <Input label="Downtime (minutes)" type="number" min={0} value={signOffDowntime} onChange={e => setSignOffDowntime(e.target.value)} placeholder="e.g. 30" />
          </div>
          {signOff.error && <p className="text-sm text-red-600">{(signOff.error as Error).message}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setSignOffId(null)}>Cancel</Button>
            <Button onClick={() => signOff.mutate()} disabled={signOff.isPending}>
              {signOff.isPending ? 'Submitting…' : 'Submit Sign-off'}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectId} onClose={() => setRejectId(null)} title="Reject Work Order">
        <div className="flex flex-col gap-3">
          <Textarea label="Rejection Reason" value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Explain why this is being rejected…" />
          {reject.error && <p className="text-sm text-red-600">{(reject.error as Error).message}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setRejectId(null)}>Cancel</Button>
            <Button variant="danger" onClick={() => reject.mutate()} disabled={reject.isPending}>
              {reject.isPending ? 'Rejecting…' : 'Reject'}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Parts panel */}
      <Dialog
        open={!!partsWoId}
        onClose={() => { setPartsWoId(null); setAddPartId(''); setAddPartQty(1); }}
        title={`Parts — ${partsWoTitle}`}
        className="max-w-lg"
      >
        <div className="flex flex-col gap-4">
          {/* Current parts list */}
          {woParts.length === 0 ? (
            <p className="text-sm text-slate-400">No parts added yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-slate-500 text-xs uppercase">
                  <th className="text-left py-1 pr-3">Part</th>
                  <th className="text-right py-1 pr-3">Qty</th>
                  <th className="text-right py-1 pr-3">Unit Cost</th>
                  <th className="text-right py-1">Subtotal</th>
                  {isSupervisor && <th />}
                </tr>
              </thead>
              <tbody>
                {woParts.map(wp => {
                  const cost = wp.part?.unit_cost ? Number(wp.part.unit_cost) : null;
                  return (
                    <tr key={wp.part_id} className="border-b last:border-0">
                      <td className="py-2 pr-3">
                        <span className="font-medium">{wp.part?.name ?? wp.part_id}</span>
                        <span className="text-xs text-slate-400 ml-1">({wp.part?.part_number})</span>
                      </td>
                      <td className="text-right py-2 pr-3">{wp.quantity_used}</td>
                      <td className="text-right py-2 pr-3">
                        {cost !== null ? `$${cost.toFixed(2)}` : '—'}
                      </td>
                      <td className="text-right py-2">
                        {cost !== null ? `$${(cost * wp.quantity_used).toFixed(2)}` : '—'}
                      </td>
                      {isSupervisor && (
                        <td className="py-2 pl-2">
                          <button
                            onClick={() => removePart.mutate({ woId: partsWoId!, partId: wp.part_id })}
                            className="text-red-400 hover:text-red-600 text-xs"
                          >Remove</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {woParts.some(wp => wp.part?.unit_cost) && (
                <tfoot>
                  <tr>
                    <td colSpan={3} className="text-right py-2 font-medium text-sm">Total:</td>
                    <td className="text-right py-2 font-bold">
                      ${woParts.reduce((sum, wp) => {
                        const c = wp.part?.unit_cost ? Number(wp.part.unit_cost) : 0;
                        return sum + c * wp.quantity_used;
                      }, 0).toFixed(2)}
                    </td>
                    {isSupervisor && <td />}
                  </tr>
                </tfoot>
              )}
            </table>
          )}

          {/* Add part form — admin/supervisor always, tech if WO is theirs */}
          {(isSupervisor || (isTechOnly && partsWo?.assigned_technician_id === user?.id)) && (
            <div className="border-t pt-3 flex gap-2 items-end">
              <div className="flex-1">
                <Select
                  label="Add Part"
                  value={addPartId}
                  onChange={e => setAddPartId(e.target.value)}
                >
                  <option value="">Select part…</option>
                  {allParts?.data.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.part_number} — {p.name} (stock: {p.stock_quantity})
                    </option>
                  ))}
                </Select>
              </div>
              <div className="w-20">
                <Input
                  label="Qty"
                  type="number"
                  min={1}
                  value={addPartQty}
                  onChange={e => setAddPartQty(parseInt(e.target.value) || 1)}
                />
              </div>
              <Button
                size="sm"
                onClick={() => addPart.mutate()}
                disabled={!addPartId || addPart.isPending}
              >
                {addPart.isPending ? '…' : 'Add'}
              </Button>
            </div>
          )}
          {addPart.error && (
            <p className="text-sm text-red-600">{(addPart.error as Error).message}</p>
          )}
        </div>
      </Dialog>
    </div>
  );
}
